// frontend/src/components/TouristComposer.jsx
//
// Redesigned — searchable nationality picker, animated guest cards,
// type selector with icons, matches BookingPage design tokens.

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_NATIONALITIES = gql`
  query GetNationalities {
    countries { code name visitorClassification }
  }
`;

/* ─── Design tokens (mirrors BookingPage) ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.12)';
const NAVY     = '#080e1a';

/* ─── Classification map ─── */
const CLASSIFICATION_MAP = {
  citizen:         { label: 'East Africa — Citizen',  order: 0 },
  east_africa:     { label: 'East Africa — Resident', order: 1 },
  east_african:    { label: 'East Africa — Resident', order: 1 },
  resident:        { label: 'East Africa — Resident', order: 1 },
  southern_africa: { label: 'Southern Africa',        order: 2 },
  west_africa:     { label: 'West Africa',            order: 3 },
  other_africa:    { label: 'Africa — Other',         order: 4 },
  international:   { label: 'International',          order: 5 },
};

function groupCountries(countries) {
  const groups = {};
  for (const c of countries) {
    const cls  = (c.visitorClassification || 'international').toLowerCase().trim();
    const meta = CLASSIFICATION_MAP[cls] || { label: 'International', order: 5 };
    if (!groups[meta.label]) groups[meta.label] = { order: meta.order, items: [] };
    groups[meta.label].items.push(c);
  }
  for (const g of Object.values(groups)) g.items.sort((a, b) => a.name.localeCompare(b.name));
  // Always return [label, items[]] — flat array as second element, not { order, items }
  return Object.entries(groups)
    .sort((a, b) => a[1].order - b[1].order)
    .map(([label, { items }]) => [label, items]);
}

/* ─── Searchable nationality combobox ─── */
export function NationalityCombobox({ value, onChange, countries, placeholder = 'Search nationality…' }) {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const inputRef            = useRef(null);
  const listRef             = useRef(null);
  const containerRef        = useRef(null);
  const [dropStyle, setDropStyle] = useState({});

  const groups = useMemo(() => groupCountries(countries || []), [countries]);

  const allItems = useMemo(() =>
    groups.flatMap(([label, items]) => items.map(c => ({ ...c, group: label }))),
    [groups]
  );

  const selected = useMemo(() =>
    allItems.find(c => c.code === value) || null,
    [allItems, value]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return groups;
    const q = query.toLowerCase();
    const matched = allItems.filter(c => c.name.toLowerCase().includes(q));
    const g = {};
    for (const c of matched) {
      if (!g[c.group]) g[c.group] = [];
      g[c.group].push(c);
    }
    return Object.entries(g);
  }, [query, groups, allItems]);

  const totalResults = useMemo(() =>
    filtered.reduce((n, [, items]) => n + items.length, 0),
    [filtered]
  );

  /* Compute dropdown position to avoid overflow on mobile */
  const computeDropStyle = useCallback(() => {
    if (!containerRef.current) return {};
    const rect = containerRef.current.getBoundingClientRect();
    const vw   = window.innerWidth;
    const dropW = Math.min(320, vw - 16);
    // If container right edge would clip, align to right of viewport with margin
    let left = 0;
    if (rect.left + dropW > vw - 8) {
      left = -(rect.left - (vw - dropW - 8));
    }
    return { width: dropW + 'px', left: left + 'px', right: 'auto' };
  }, []);

  const openDropdown = () => {
    setDropStyle(computeDropStyle());
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const selectItem = useCallback((code) => {
    onChange(code);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>

      {/* Trigger / display */}
      <button
        type="button"
        onClick={openDropdown}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          fontFamily: "'Jost', sans-serif",
          fontSize: '16px',
          color: selected ? '#0f172a' : '#b0aaa0',
          background: '#fff',
          border: `1px solid ${value ? '#ddd9d2' : 'rgb(252 211 77)'}`,
          padding: '11px 14px',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'border-color 0.2s',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.borderColor = GOLD; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.borderColor = value ? '#ddd9d2' : 'rgb(252 211 77)'; }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selected ? selected.name : placeholder}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onMouseDown={clear}
              onKeyDown={e => e.key === 'Enter' && clear(e)}
              style={{
                width: '16px', height: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%',
                background: 'rgba(100,116,139,0.15)',
                color: '#12100e',
                fontSize: '13px',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              aria-label="Clear nationality"
            >
              ✕
            </span>
          )}
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
            color: GOLD,
          }}>
            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: dropStyle.left || 0,
            width: dropStyle.width || '100%',
            background: '#fff',
            border: `1px solid ${GOLD}`,
            boxShadow: '0 8px 32px rgba(8,14,26,0.12)',
            zIndex: 9999,
            animation: 'ncDropIn 0.18s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {/* Search input */}
          <div style={{
            padding: '10px 12px',
            borderBottom: '1px solid #f0ece6',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ color: '#12100e', flexShrink: 0 }}>
              <circle cx="5.5" cy="5.5" r="4.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M9 9L12 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type to search…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontFamily: "'Jost', sans-serif",
                fontSize: '16px',
                color: '#0f172a',
                background: 'transparent',
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#12100e', fontSize: '15px', padding: 0 }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Results list */}
          <div
            ref={listRef}
            style={{ maxHeight: '260px', overflowY: 'auto', overscrollBehavior: 'contain' }}
          >
            {totalResults === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                fontFamily: "'Jost', sans-serif",
                fontSize: '15px',
                color: '#12100e',
              }}>
                No results for "{query}"
              </div>
            ) : (
              filtered.map(([label, items]) => (
                <div key={label}>
                  <div style={{
                    padding: '8px 12px 4px',
                    fontFamily: "'Jost', sans-serif",
                    fontSize: '13px',
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: GOLD,
                    background: 'rgba(184,151,90,0.05)',
                    borderTop: '1px solid #f0ece6',
                  }}>
                    {label}
                  </div>
                  {items.map(c => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => selectItem(c.code)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '9px 14px',
                        border: 'none',
                        background: value === c.code ? GOLD_DIM : 'transparent',
                        fontFamily: "'Jost', sans-serif",
                        fontSize: '16px',
                        color: value === c.code ? NAVY : '#334155',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => { if (value !== c.code) e.currentTarget.style.background = '#fafaf8'; }}
                      onMouseLeave={e => { if (value !== c.code) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span>{c.name}</span>
                      {value === c.code && (
                        <svg width="11" height="8" viewBox="0 0 11 8" fill="none" style={{ color: GOLD }}>
                          <path d="M1 4L4 7L10 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>

          {/* Footer hint */}
          <div style={{
            padding: '8px 12px',
            borderTop: '1px solid #f0ece6',
            fontFamily: "'Jost', sans-serif",
            fontSize: '13px',
            color: '#12100e',
            letterSpacing: '0.1em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span>{totalResults} {totalResults === 1 ? 'country' : 'countries'}</span>
            <span>ESC to close</span>
          </div>
        </div>
      )}

    </div>
  );
}

/* ─── Tourist type selector with icons ─── */
const TYPE_CONFIG = [
  {
    value: 'adult',
    label: 'Adult',
    sub: '16+',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M2.5 14c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    value: 'child',
    label: 'Child',
    sub: '5–15',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="4" r="2" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M3.5 14c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M5 7.5h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    value: 'infant',
    label: 'Infant',
    sub: 'Under 5',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M5 9.5c0 0-2 1-2 3h10c0-2-2-3-2-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.5 5h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
];

export function TouristTypeSelector({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '6px' }}>
      {TYPE_CONFIG.map(t => {
        const active = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
              height: '44px',
              padding: '0 6px',
              border: `1px solid ${active ? GOLD : '#ddd9d2'}`,
              background: active ? GOLD_DIM : '#fff',
              color: active ? NAVY : '#64748b',
              cursor: 'pointer',
              transition: 'all 0.18s',
              fontFamily: "'Jost', sans-serif",
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = 'rgba(184,151,90,0.5)'; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = '#ddd9d2'; }}
          >
            <span style={{ color: active ? GOLD : '#94a3b8', transition: 'color 0.18s', display: 'flex', flexShrink: 0 }}>
              {t.icon}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {t.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Single guest card ─── */
function GuestCard({ tourist, idx, onChange, countries, onRemove, canRemove }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), idx * 60);
    return () => clearTimeout(t);
  }, [idx]);

  const isLead = idx === 0;

  return (
    <div
      style={{
        border: `1px solid ${isLead ? 'rgba(184,151,90,0.4)' : '#e8e4de'}`,
        background: isLead ? 'rgba(184,151,90,0.03)' : '#fff',
        padding: '18px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(8px)',
        transition: 'opacity 0.35s cubic-bezier(0.22,1,0.36,1), transform 0.35s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Numbered badge */}
          <div style={{
            width: '24px', height: '24px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${isLead ? GOLD : '#ddd9d2'}`,
            background: isLead ? GOLD_DIM : 'transparent',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '16px',
            fontWeight: 400,
            color: isLead ? GOLD : '#94a3b8',
            fontStyle: 'normal',
            flexShrink: 0,
          }}>
            {idx + 1}
          </div>
          <div>
            <span style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#12100e',
            }}>
              Guest {idx + 1}
            </span>
            {isLead && (
              <span style={{
                marginLeft: '8px',
                fontFamily: "'Jost', sans-serif",
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '0.05em',
                color: GOLD,
              }}>
                · Group lead
              </span>
            )}
          </div>
        </div>

        {/* Remove button — shown for non-lead when canRemove */}
        {!isLead && canRemove && (
          <button
            type="button"
            onClick={onRemove}
            style={{
              width: '24px', height: '24px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid #e8e4de',
              background: 'transparent',
              color: '#12100e',
              cursor: 'pointer',
              transition: 'all 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#fca5a5';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#e8e4de';
              e.currentTarget.style.color = '#94a3b8';
            }}
            aria-label={`Remove guest ${idx + 1}`}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 2L8 8M8 2L2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* Fields */}
      <div className="tc-fields-grid">
        {/* Type */}
        <div>
          <label style={{
            display: 'block',
            fontFamily: "'Jost', sans-serif",
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#12100e',
            marginBottom: '8px',
          }}>
            Type
          </label>
          <TouristTypeSelector
            value={tourist.touristType}
            onChange={v => onChange('touristType', v)}
          />
        </div>

        {/* Nationality */}
        <div>
          <label style={{
            display: 'block',
            fontFamily: "'Jost', sans-serif",
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#12100e',
            marginBottom: '8px',
          }}>
            Nationality
            {isLead && (
              <span style={{
                marginLeft: '4px',
                fontFamily: "'Jost', sans-serif",
                fontSize: '13px',
                fontWeight: 400,
                letterSpacing: 0,
                textTransform: 'none',
                color: GOLD,
              }}>
                — affects park fees
              </span>
            )}
          </label>
          <NationalityCombobox
            value={tourist.nationality}
            onChange={code => onChange('nationality', code)}
            countries={countries}
          />
          {!tourist.nationality && (
            <p style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: '13px',
              marginTop: '5px',
              color: GOLD,
              letterSpacing: '0.05em',
            }}>
              Required for accurate pricing
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Counter button ─── */
function CounterBtn({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '36px', height: '36px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #ddd9d2',
        background: '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: disabled ? '#cbd5e1' : '#334155',
        fontFamily: "'Jost', sans-serif",
        fontSize: '22px',
        fontWeight: 300,
        transition: 'all 0.15s',
        flexShrink: 0,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.borderColor = GOLD; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.borderColor = '#ddd9d2'; }}
    >
      {children}
    </button>
  );
}

/* ─── Main TouristComposer ─── */
export function TouristComposer({ tourists, onChange, maxTourists = 20 }) {
  const { data: geoData } = useQuery(GET_NATIONALITIES, { fetchPolicy: 'cache-first' });
  const countries = geoData?.countries || [];

  const add = () => {
    if (tourists.length >= maxTourists) return;
    onChange([...tourists, { touristType: 'adult', nationality: '' }]);
  };

  const remove = () => {
    if (tourists.length <= 1) return;
    onChange(tourists.slice(0, -1));
  };

  const removeAt = (idx) => {
    onChange(tourists.filter((_, i) => i !== idx));
  };

  const update = (idx, field, value) => {
    onChange(tourists.map((t, i) => {
      if (i !== idx) return t;
      // When type changes, clear nationality — the fee category changes
      // and the old nationality may no longer reflect the correct rate band
      if (field === 'touristType') {
        return { ...t, touristType: value, nationality: '' };
      }
      return { ...t, [field]: value };
    }));
  };

  const allComplete = tourists.every(t => t.nationality);
  const filledCount = tourists.filter(t => t.nationality).length;

  return (
    <div>
      {/* ── Mobile styles ── */}
      <style>{`
        .tc-fields-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (max-width: 480px) {
          .tc-fields-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
        }
        @keyframes ncDropIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {/* ── Counter row ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px 20px',
        border: '1px solid #e8e4de',
        background: '#fafaf8',
      }}>
        <CounterBtn onClick={remove} disabled={tourists.length <= 1}>−</CounterBtn>

        <div style={{ flex: 1, textAlign: 'center' }}>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '48px',
            fontWeight: 300,
            color: '#0f172a',
            lineHeight: 1,
            margin: 0,
          }}>
            {tourists.length}
          </p>
          <p style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: '13px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#12100e',
            marginTop: '4px',
          }}>
            {tourists.length === 1 ? 'Traveller' : 'Travellers'}
          </p>
        </div>

        <CounterBtn onClick={add} disabled={tourists.length >= maxTourists}>+</CounterBtn>
      </div>

      {/* ── Progress indicator (when some are incomplete) ── */}
      {!allComplete && tourists.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#12100e',
            }}>
              Nationalities set
            </span>
            <span style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: '13px',
              color: GOLD,
              fontWeight: 500,
            }}>
              {filledCount} / {tourists.length}
            </span>
          </div>
          <div style={{ height: '2px', background: '#ede9e2', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              background: GOLD,
              width: `${(filledCount / tourists.length) * 100}%`,
              transition: 'width 0.4s cubic-bezier(0.22,1,0.36,1)',
            }} />
          </div>
        </div>
      )}

      {/* ── Guest cards ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {tourists.map((t, idx) => (
          <GuestCard
            key={idx}
            idx={idx}
            tourist={t}
            countries={countries}
            onChange={(field, value) => update(idx, field, value)}
            onRemove={() => removeAt(idx)}
            canRemove={tourists.length > 1}
          />
        ))}
      </div>

      {/* ── Footer notes ── */}
      <p style={{
        fontFamily: "'Jost', sans-serif",
        fontSize: '13px',
        color: '#12100e',
        marginTop: '12px',
        lineHeight: 1.6,
      }}>
        Children 5–15 pay reduced park fees · infants under 5 are free · fees vary per park and nationality
      </p>

      {/* ── Completion nudge ── */}
      {!allComplete && (
        <div style={{
          marginTop: '12px',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          border: '1px solid rgba(184,151,90,0.3)',
          background: 'rgba(184,151,90,0.05)',
        }}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ color: GOLD, flexShrink: 0 }}>
            <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M6.5 4v3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <circle cx="6.5" cy="9.5" r="0.6" fill="currentColor"/>
          </svg>
          <span style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: '13px',
            color: GOLD,
          }}>
            Set nationality for all guests to get accurate pricing
          </span>
        </div>
      )}
    </div>
  );
}